<template>
    <SearchCardLayout
        v-if="props.data"
        class="scene-card"
        :archived="isArchivedScene"
        :disabled="!userPermissionConfig.canOpenReview">
        <template #left>
            <ArboThumbnail
                v-arbo-tooltip="{ content: 'Full view', hideOnClick: false, delay: [500, 0] }"
                style="max-width: 92px; width: 92px"
                class="arbo-ui-styles"
                v-bind="{ ...thumbnailProps }"
                :thumbnail="props.data?.thumbnail || ''"
                :size="'32px'"
                :disabled="false"
                :enableMetaKeyOpen="true"
                @click.stop.prevent="openHub"
                @onNormalClick="handleOpenhub"
                @onCommandClick="handleOpenHubNewTab" />
        </template>
        <template #quick-action>
            <ArboButton
                v-if="userPermissionConfig.canOpenReview"
                class="arbo-ui-styles"
                :label="'Open Review'"
                :prefix-icon="'PenRuler'"
                size="small"
                @click.stop="openReview" />
        </template>
        <template #more-actions="{ show, hide }">
            <TessMoreActions class="arbo-ui-styles" v-bind="sceneMoreActions" size="small" @show="show" @hide="hide" />
        </template>
        <template #detail="{ width }">
            <div class="scene-detail">
                <div class="breadcrumbs" :class="{ 'has-archived': isArchivedScene }">
                    <ArboIcon v-if="isArchivedScene" name="BoxArchive" />
                    <TessBreadcrumb
                        class="arbo-ui-styles"
                        v-bind="{ ...breadcrumbTemp, width: width, disabled: true }" />
                </div>
                <div class="row">
                    <div v-arbo-tooltip="{ content: props.data?.name, delay: [1000, 0] }" class="scene-name">
                        {{ props.data?.name }}
                    </div>
                    <TessAvatarGroup
                        v-if="assignedUsers.data.length > 0"
                        class="arbo-ui-styles"
                        v-bind="assignedUsers" />
                </div>
            </div>
        </template>
        <template #status>
            <div v-arbo-tooltip="{ content: statusTooltipContent, delay: [500, 0] }">
                <TessHoverTile
                    class="arbo-ui-styles hover-tile"
                    :disabled="true"
                    :background-color="currentStatusColor"
                    :height="52"
                    :width="16"
                    :layout-color="props.data?.template_flow_status?.color" />
            </div>
        </template>
    </SearchCardLayout>
    <SceneSkeleton v-else />
</template>
<script setup lang="ts">
import SceneSkeleton from '../skeleton-loading/SceneSkeleton.vue';
import SearchCardLayout from './CardLayout.vue';
import type { SceneCardDataType } from 'src/types/scene.types';
import { computed, inject } from 'vue';
import { createCommonLink } from '@arbocollab/arbo-ui-components';
import type { ThumbnailPropsType } from '@arbocollab/arbo-ui-components/dist/src/types/sceneCardTypes';
import { baseLinkConfig, clickActionHandler } from 'src/plugins';
import { storeToRefs } from 'pinia';

import { useUserStore } from 'src/stores/userStore';
import type { UserInformation } from 'src/types/user.types';
type Props = {
    data: SceneCardDataType;
};
import { tooltip } from '@arbocollab/arbo-ui-components';
const vArboTooltip = tooltip;

const takeId = computed(() => props.data?.entity_path?.[props.data?.entity_path.length - 1]?.id);
const linkConfig = computed(() => {
    const config = {
        ...baseLinkConfig(),
        domain: props.data?.id,
        entity_id: props.data?.entity_id || '',
        scene_id: props.data?.id || '',
        take_id: takeId.value || '',
        woohoo_id: takeId.value || ''
    };
    return {
        hub: createCommonLink({ type: 'scene_hub', config }).url,
        review: createCommonLink({ type: 'review_scene', config }).url,
        take_prk: createCommonLink({ type: 'project', config }).url,
        take_review: createCommonLink({ type: 'review_take', config }).url,
        take_update: createCommonLink({ type: 'update', config }).url,
        take_woohoo: createCommonLink({ type: 'woohoo', config }).url
    };
});
const thumbnailProps = computed<ThumbnailPropsType>(() => ({
    thumbnail: props.data?.thumbnail || '',
    icon: {
        iconName: 'CustomSceneHub',
        url: '',
        backgroundColor: '#3c9bfa'
    },
    footerLabel: undefined,
    disabled: false,
    size: 'medium',
    disableHover: false
}));
const openReview = (e: MouseEvent) => {
    if (e.metaKey || e.ctrlKey) {
        const link = linkConfig.value.review;
        window.open(link, '_blank');
        return;
    } else {
        window.location.href = linkConfig.value.review;
    }
};

const switchInstance = inject('switchInstance') as any;
const handleOpenhub = (_: string, event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    openHub(event);
};
const handleOpenHubNewTab = () => {
    window.open(linkConfig.value.hub, '_blank');
};
const openHub = (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (e.metaKey || e.ctrlKey) {
        const link = linkConfig.value.hub;
        window.open(link, '_blank');
        return;
    } else {
        if (switchInstance) {
            switchInstance('scene_hub', { tab: 'home', id: props.data.id }, { showLoading: false });
            return;
        } else {
            console.error('No switchInstance found');
        }
        // {window.location.href = linkConfig.value.hub;}
    }
};
const openTakeIn = (e: MouseEvent, type: 'take_prk' | 'take_review' | 'take_update' | 'take_woohoo') => {
    const link = linkConfig.value[type];
    clickActionHandler(e, link, {});
};
const copyLinkReview = () => {
    const link = linkConfig.value.review;
    navigator.clipboard.writeText(link);
};
const copyLinkHub = () => {
    const link = linkConfig.value.hub;
    navigator.clipboard.writeText(link);
};
const openProjectDirectory = (e: MouseEvent) => {
    const link = `${linkConfig.value.take_prk}?f=${props.data.id}`;
    clickActionHandler(e, link, {});
};
const { userPermissionConfig } = storeToRefs(useUserStore());
const props = defineProps<Props>();

const sceneMoreActions = computed(() => {
    const actionTop = [
        {
            id: '1',
            label: '',
            icon: 'CustomSceneHub',
            action: openHub,
            visible: true,
            preventClose: false,
            tooltip: 'Full view',
            type: 'action'
        },
        {
            id: '2',
            label: '',
            icon: 'PenRuler',
            action: openReview,
            visible: userPermissionConfig.value.canOpenReview,
            tooltip: 'Open Review',
            type: 'action',
            preventClose: false
        }
    ].filter((item) => item.visible);
    const { canOpenProject, canOpenReview, canOpenTakeWoohoo, canOpenTakeUpdate } = userPermissionConfig.value;
    const menuItems = [
        {
            items: [
                {
                    id: '3',
                    label: 'Copy link',
                    icon: 'Link',

                    visible: true,
                    type: 'submenu',
                    subMenu: [
                        {
                            id: '4',
                            label: 'Review',
                            icon: 'PenRuler',
                            action: copyLinkReview,
                            visible: canOpenProject,
                            type: 'action'
                        },
                        {
                            id: '5',
                            label: 'Full view',
                            icon: 'CustomSceneHub',
                            action: copyLinkHub,
                            visible: true,
                            type: 'action'
                        }
                    ]
                },
                {
                    id: '6',
                    label: 'Open Project Directory',
                    icon: 'Sitemap',
                    action: openProjectDirectory,
                    visible: canOpenProject,
                    type: 'action'
                },
                {
                    id: 'open_take',
                    label: 'Open in',
                    icon: 'ArrowUpRightFromSquare',
                    visible: canOpenProject || canOpenReview || canOpenTakeWoohoo || canOpenTakeUpdate,
                    type: 'submenu',
                    subMenu: [
                        {
                            id: 'pj-dir',
                            label: 'Project Directory',
                            icon: 'Sitemap',
                            action: (e: MouseEvent) => openTakeIn(e, 'take_prk'),
                            visible: canOpenProject,
                            type: 'action'
                        },
                        {
                            id: 'take_review',
                            label: 'Review',
                            icon: 'PenRuler',
                            action: openReview,
                            visible: canOpenReview,
                            type: 'action'
                        },
                        {
                            id: 'update',
                            label: 'Update',
                            icon: 'UserCheck',
                            action: (e: MouseEvent) => openTakeIn(e, 'take_update'),
                            visible: canOpenTakeUpdate,
                            type: 'action'
                        },
                        {
                            id: 'pj-dir',
                            label: 'Woohoo Details',
                            icon: 'ChartPie',
                            action: (e: MouseEvent) => openTakeIn(e, 'take_woohoo'),
                            visible: canOpenTakeWoohoo,
                            type: 'action'
                        }
                    ]
                }
            ]
        }
    ];
    return {
        actionsTop: actionTop,
        menuItems: menuItems,
        tooltip: 'More actions',
        icon: 'EllipsisVertical'
    };
});

const isArchivedScene = computed(() => props.data?.property?.is_archived === 1);
const breadcrumbTemp = computed(() => ({
    url: createCommonLink({
        type: 'scene_hub',
        config: {
            ...baseLinkConfig(),
            scene_id: props.data?.id || ''
        }
    }).url,
    items: [
        ...(props.data?.entity_path?.map((item) => {
            return {
                name: item.name,
                id: item.id,
                url: createCommonLink({
                    type: 'project',
                    config: {
                        ...baseLinkConfig(),
                        entity_id: item.id
                    }
                }).url,
                isHighlight: false
            };
        }) || [])
    ],
    disabled: false,
    replaceCurrent: false,
    separator: '>',
    lastItemWidth: 150,
    canCopy: true,
    width: 350
}));
const userStore = useUserStore();
const { userInformation } = storeToRefs(userStore);
const userId = computed(() => userInformation.value?.id || '');
const convertAssigneeUser = (users?: UserInformation[]) => {
    return (
        users?.map((user) => ({
            ...user,
            disableHover: true,
            is_me: user.user_id == userId.value
        })) || []
    );
};
const assignedUsers = computed(() => {
    const assignedUsers = props.data?.assignees || [];
    return {
        data: convertAssigneeUser(assignedUsers),
        labelKey: 'display_name',
        tooltipKey: 'display_name',
        iconKey: 'icon',
        imageKey: 'profile_image',
        isMeKey: 'is_me',
        disableHover: true,
        bgColorKey: 'bg_color',
        textColorKey: 'text_color',
        isOpenNewTabKey: 'is_open_new_tab',
        bgColor: '',
        textColor: '',
        gap: 0,
        displayLimit: 4,
        isUseShortLabel: true,
        shortLabelKey: 'initial'
    };
});
const currentStatusColor = computed(() => props.data?.template_flow_status?.color);
const statusTooltipContent = computed(() => props.data?.template_flow_status?.name);
</script>
